import { useState } from "react";
import { BsQuestionCircle } from "react-icons/bs";
import type { Address, Card, CardClass, Position } from "../data/types";
import { t } from "../utils/i18n";
import { WINDOW_COLS, getCustomScreenCards } from "../utils/sssLogic";
import { CardCell } from "./CardCell";

type Intensity = "light" | "dark";

// Pattern string → per-slot overlay intensity (0-indexed).
// Patterns confirmed to have NO overlay are explicitly registered as {}.
// Patterns absent from this map are UNCONFIRMED — not the same as no overlay.
const OVERLAY_MAP: Record<string, Partial<Record<number, Intensity>>> = {
  SSSMSM: { 4: "light", 5: "dark" },
  SSMSMS: { 2: "light", 4: "light" },
  SSGSSS: { 2: "light" },
  GSSSGS: { 1: "light", 4: "light" },
  GSSSSS: { 1: "light" },
  MSMSSS: { 1: "light", 2: "dark" },
  SSSMSG: { 4: "light", 5: "dark" },
  SSSSSG: { 5: "light" },
  SGSSSG: { 5: "light" },
  SMSSSM: { 5: "light" },
  SSSSSS: {},
  SSSSGS: {},
  SSSGSS: { 4: "light" },
  SMSGSS: { 3: "light", 4: "dark" },
  SMSSSS: {},
  SSSSMS: {},
  MSGSSS: { 1: "light", 2: "dark" },
  SGSSSS: {},
  SMSSSG: { 5: "light" },
  GSMSSS: { 1: "light", 2: "dark" },
  MSSSSS: { 1: "light" },
  GSSSMS: { 1: "light", 3: "light" },
  MSSSMS: { 1: "light", 4: "light" },
  MSSSGS: { 1: "light", 4: "light" },
  SSSGSM: { 4: "light", 5: "dark" },
  SSMSSS: { 4: "light" },
  SSSMSS: { 4: "light" },
  SSSSSM: { 5: "light" },
};

interface Props {
  cards: Card[];
  position: Position;
  address: Address;
  spriteSrc: string;
}

const POSITIONS = [1, 2, 3, 4, 5, 6] as const;
const ADDRESSES = ["A", "B", "C"] as const;

function isServerComplete(cards: readonly Card[]): boolean {
  for (const pos of POSITIONS) {
    for (const addr of ADDRESSES) {
      const slots = getCustomScreenCards(cards, pos, addr);
      const pat = slots.map((s) => s.card.class as CardClass).join("");
      if (!(pat in OVERLAY_MAP)) return false;
    }
  }
  return true;
}

export function CustomScreen({ cards, position, address, spriteSrc }: Props) {
  const slots = getCustomScreenCards(cards, position, address);
  const pattern = slots.map((s) => s.card.class as CardClass).join("");
  const confirmedConfig = pattern in OVERLAY_MAP ? OVERLAY_MAP[pattern] : null;
  const serverComplete = isServerComplete(cards);
  const hasOverlays =
    serverComplete &&
    confirmedConfig !== null &&
    Object.keys(confirmedConfig).length > 0;
  const [showOverlay, setShowOverlay] = useState(false);

  return (
    <div className="w-full sm:w-auto">
      <h3 className="mb-1.5 flex items-center justify-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-600 sm:justify-start">
        <span>{t("customScreen")}</span>
        <a
          href="https://x.com/ssr_haijin/status/2054109404107350265?s=46"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("customScreenHelp")}
          title={t("customScreenHelp")}
          className="inline-flex text-sky-600 hover:text-sky-700"
        >
          <BsQuestionCircle aria-hidden className="h-3.5 w-3.5" />
        </a>
        {hasOverlays && (
          <button
            type="button"
            role="switch"
            aria-checked={showOverlay}
            onClick={() => setShowOverlay((v) => !v)}
            title={showOverlay ? "キューの奥を非表示" : "キューの奥を表示"}
            className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
              showOverlay ? "bg-slate-600" : "bg-slate-300"
            }`}
          >
            <span
              className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
                showOverlay ? "translate-x-3.5" : "translate-x-0.5"
              }`}
            />
          </button>
        )}
      </h3>
      <div
        className="grid w-full gap-1.5 rounded border border-slate-300 bg-slate-900/90 p-2 sm:inline-grid sm:w-auto"
        style={{
          gridTemplateColumns: `repeat(${WINDOW_COLS}, minmax(0, 1fr))`,
        }}
      >
        {slots.map((s, i) => {
          const intensity =
            showOverlay && confirmedConfig ? confirmedConfig[i] : undefined;
          const overlayClass =
            intensity === "dark"
              ? "bg-black/70"
              : intensity === "light"
                ? "bg-black/40"
                : null;
          return (
            <div key={i} className="relative">
              <CardCell
                name={s.card.name}
                cardClass={s.card.class}
                variant="in-window"
                size="md"
                spriteSrc={spriteSrc}
                poolRow={s.poolRow}
                poolCol={s.poolCol}
              />
              {overlayClass && (
                <div
                  className={`absolute inset-0 rounded pointer-events-none ${overlayClass}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
